/**
 * Google Analytics 4 utilities
 * Only tracks in production (splitfiesta.com)
 */

const GA_MEASUREMENT_ID = 'G-P05P1G6NPE';

/** Check if we're in production */
const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'splitfiesta.com' || hostname === 'www.splitfiesta.com';
};

/** Initialize GA4 - call once on app load */
export const initGA = (): void => {
  if (!isProduction()) return;

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];

  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually on route change
  });
};

/** Track page view */
export const trackPageView = (path: string): void => {
  if (!isProduction() || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: document.title,
  });
};

/** Track custom event */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (!isProduction() || !window.gtag) return;

  window.gtag('event', eventName, params);
};

// ============================================
// Type-safe event tracking functions
// ============================================

/** Auth events */
export const trackSignUp = (method: 'email' | 'google' | 'apple'): void => {
  trackEvent('sign_up', { method });
};

export const trackLogin = (
  method: 'email' | 'google' | 'apple' | 'passkey'
): void => {
  trackEvent('login', { method });
};

export const trackLogout = (): void => {
  trackEvent('logout');
};

/** Group events */
export const trackGroupCreated = (groupId: string): void => {
  trackEvent('group_created', { group_id: groupId });
};

export const trackGroupUpdated = (groupId: string): void => {
  trackEvent('group_updated', { group_id: groupId });
};

export const trackGroupDeleted = (groupId: string): void => {
  trackEvent('group_deleted', { group_id: groupId });
};

export const trackGroupJoined = (groupId: string): void => {
  trackEvent('group_joined', { group_id: groupId });
};

export const trackGroupLeft = (groupId: string): void => {
  trackEvent('group_left', { group_id: groupId });
};

export const trackMemberRemoved = (groupId: string): void => {
  trackEvent('member_removed', { group_id: groupId });
};

/** Expense events */
export const trackExpenseCreated = (
  groupId: string,
  amount: number,
  currency: string,
  category?: string
): void => {
  trackEvent('expense_created', {
    group_id: groupId,
    value: amount,
    currency,
    category: category || 'uncategorized',
  });
};

export const trackExpenseUpdated = (
  groupId: string,
  expenseId: string
): void => {
  trackEvent('expense_updated', { group_id: groupId, expense_id: expenseId });
};

export const trackExpenseDeleted = (
  groupId: string,
  expenseId: string
): void => {
  trackEvent('expense_deleted', { group_id: groupId, expense_id: expenseId });
};

/** Settlement events */
export const trackSettlementCreated = (
  groupId: string,
  amount: number,
  currency: string
): void => {
  trackEvent('settlement_created', {
    group_id: groupId,
    value: amount,
    currency,
  });
};

export const trackSettlementConfirmed = (
  groupId: string,
  settlementId: string
): void => {
  trackEvent('settlement_confirmed', {
    group_id: groupId,
    settlement_id: settlementId,
  });
};

export const trackSettlementRejected = (
  groupId: string,
  settlementId: string
): void => {
  trackEvent('settlement_rejected', {
    group_id: groupId,
    settlement_id: settlementId,
  });
};

export const trackPaymentRecorded = (
  groupId: string,
  amount: number,
  currency: string
): void => {
  trackEvent('payment_recorded', {
    group_id: groupId,
    value: amount,
    currency,
    initiated_by_creditor: true,
  });
};

/** Invitation events */
export const trackInviteCreated = (groupId: string): void => {
  trackEvent('invite_created', { group_id: groupId });
};

export const trackInviteAccepted = (groupId: string): void => {
  trackEvent('invite_accepted', { group_id: groupId });
};

/** Engagement events */
export const trackCommentAdded = (groupId: string, expenseId: string): void => {
  trackEvent('comment_added', { group_id: groupId, expense_id: expenseId });
};

export const trackReactionAdded = (
  groupId: string,
  expenseId: string,
  emoji: string
): void => {
  trackEvent('reaction_added', {
    group_id: groupId,
    expense_id: expenseId,
    emoji,
  });
};

/** Passkey events */
export const trackPasskeyRegistered = (): void => {
  trackEvent('passkey_registered');
};

/** Profile events */
export const trackProfileUpdated = (): void => {
  trackEvent('profile_updated');
};

export const trackSettingsUpdated = (): void => {
  trackEvent('settings_updated');
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
